import React, { ReactElement, useEffect } from 'react';
import { withScope, captureMessage, Severity } from '@sentry/browser';
import { ReactQueryConfigProvider } from 'react-query';
import { BrowserRouter as Router } from 'react-router-dom';

import { AuthProvider } from 'contexts/auth-context';
import AppRoutes from 'routes/app-routes';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  getConsentFromSearchString,
  isConsentInURLParams,
} from 'helpers/cookie-helper';
import TagManager from 'react-gtm-module';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
const GTM_ID = process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID ?? '';

const queryConfig = {
  queries: {
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error: any) => {
      withScope((scope) => {
        if (error.response.status === 500) {
          scope.setLevel(Severity.Fatal);
        } else {
          scope.setLevel(Severity.Error);
        }
        scope.setExtra('config', error.config);
        scope.setExtra('response', error.response);
        captureMessage(
          `${error.config.url} - ${error.response.data.errorSummary ||
            error.response.status}`,
        );
      });
    },
  },
};

export default function App(): ReactElement {
  const { search } = window.location;
  const consentInLocalStorage = localStorage.getItem('cookieConsent');

  /**
   * Initialize Tag Manager only if user has consented statistics
   * and doNotTrack flag is false. If user comes from WordPress site,
   * the cookie consent is read from CookieBot plugin and then
   * set to URL parameters and then to the localstorage. If she/he comes
   * from e.g. paytrail after payment, those URL parameters are not provided
   * and therefore consent must be read from localstorage.
   */
  useEffect(() => {
    let consent;
    if (isConsentInURLParams(search)) {
      consent = getConsentFromSearchString(search);
      localStorage.setItem('cookieConsent', JSON.stringify(consent));
    } else if (consentInLocalStorage) {
      consent = JSON.parse(consentInLocalStorage);
    }

    if (consent?.statistics && !consent.doNotTrack) {
      TagManager.initialize({ gtmId: GTM_ID });
    }
  }, [search, consentInLocalStorage]);

  return (
    <div className='app'>
      <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
        <ReactQueryConfigProvider config={queryConfig}>
          <AuthProvider>
            <Router>
              <AppRoutes />
            </Router>
          </AuthProvider>
        </ReactQueryConfigProvider>
      </GoogleReCaptchaProvider>
    </div>
  );
}
